import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  Box, IconButton, HStack, Tag, TagLabel, TagCloseButton, VStack, useToast,
  Popover, PopoverTrigger, PopoverContent, PopoverBody, Text,
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { isBlankHtml } from "../../utils/messageHtml";
import { validateNewFiles, formatBytes } from "../../utils/chatAttachments";
import PaperclipIcon from "./PaperclipIcon";
import WorkItemLinkPicker from "./WorkItemLinkPicker";
import { workItemHref, workItemLinkText } from "../../utils/workItemRefs";
import { findMentionQuery, mentionSuggestions, buildMentionsPayload } from "./mentions";

const TOOLBAR = [
  ["bold", "italic", "underline", "strike"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "code"],
  ["clean"],
];
const FORMATS = ["bold", "italic", "underline", "strike", "list", "link", "code"];
const NO_FILES_TOAST = "chat-composer-no-files";

// Quill's own rule for pasted files: they're an image paste only when the
// clipboard has no html, or its html is nothing but that one <img>. Excel and
// Word put a picture of the copied cells/text next to the real html.
function isLoneImageHtml(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.childElementCount === 1 && doc.body.firstElementChild?.tagName === "IMG";
}

// Compact Quill editor for chat. Enter sends, Shift+Enter adds a new line.
// Files come in through the 📎 button, drag-and-drop or paste, and go out with
// the message. The draft (text + files) clears once onSend resolves, unless it
// resolves `false` or rejects — then it stays so the user can retry.
// The ref exposes addFiles(files), so a drop anywhere on the conversation
// around the composer lands in the draft too (same checks, same busy lock).
// `noFiles`, optional: why this conversation takes no files (an agent's thread,
// where the server refuses them). The composer then has no paperclip, and files
// dropped or pasted on it, or handed over through the ref, are turned away with
// that sentence. `note`, optional: a line under the composer.
// `mentionMembers`, optional: the conversation's other members ([{ user_id, name }]). Typing "@"
// then opens a list of them (plus @here / @channel, not in a DM: `isDm`); ↑/↓ move, Enter or Tab
// picks, Escape closes, and Enter never sends while it is open. A pick inserts "@Name " as text,
// and the send passes onSend a 4th argument, the `mentions` ({user_ids, here, channel}) of the
// picked people still named in the text (mentions.js); without any, onSend gets three.
const ChatComposer = forwardRef(function ChatComposer(
  { onSend, placeholder = "Message...", linkableItems = null, noFiles = null, note = null, mentionMembers = null, isDm = false },
  ref,
) {
  const hostRef = useRef(null);
  const quillRef = useRef(null);
  const sendRef = useRef(null);
  const onSendRef = useRef(onSend);
  onSendRef.current = onSend;
  const [files, setFiles] = useState([]);
  const filesRef = useRef([]);
  filesRef.current = files;
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const fileInputRef = useRef(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const toast = useToast();
  // The @list: { start, end, items, active } (start..end = "@query" in the editor), or null.
  const [mention, setMention] = useState(null);
  const mentionRef = useRef(null);
  mentionRef.current = mention;
  const membersRef = useRef(mentionMembers);
  membersRef.current = mentionMembers;
  const isDmRef = useRef(isDm);
  isDmRef.current = isDm;
  // People picked from the list, user_id → name; sent only while their "@Name" stays in the text.
  const pickedRef = useRef(new Map());
  // Escape closes the list for that "@": it stays closed until another one is typed.
  const dismissedAtRef = useRef(null);

  const closeMention = () => {
    mentionRef.current = null;
    setMention(null);
  };

  // Replaces "@que" with "@Name " (plain text) and keeps who was meant.
  const pickMention = (item) => {
    const q = quillRef.current;
    const open = mentionRef.current;
    if (!q || !open || !item) return;
    const text = `@${item.label} `;
    q.deleteText(open.start, open.end - open.start, "user");
    q.insertText(open.start, text, "user");
    q.setSelection(open.start + text.length, 0, "user");
    if (item.kind === "user") pickedRef.current.set(item.id, item.label);
    closeMention();
  };
  const pickMentionRef = useRef(pickMention);
  pickMentionRef.current = pickMention;

  const addFiles = (list) => {
    // Nothing joins a message that's already sending: the clear on success would drop it.
    if (busyRef.current) return;
    if (noFiles) {
      // Said once while it shows, however many files come.
      if (Array.from(list || []).length && !toast.isActive(NO_FILES_TOAST)) {
        toast({ id: NO_FILES_TOAST, title: noFiles, status: "warning", duration: 4000 });
      }
      return;
    }
    const { accepted, rejected } = validateNewFiles(filesRef.current, Array.from(list || []));
    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
    rejected.forEach((r) => toast({ title: `${r.name} ${r.reason}`, status: "warning", duration: 4000 }));
  };
  // The paste listener is set up once inside the Quill effect; the ref keeps
  // it calling the latest addFiles.
  const addFilesRef = useRef(addFiles);
  addFilesRef.current = addFiles;
  useImperativeHandle(ref, () => ({ addFiles: (list) => addFilesRef.current(list) }), []);

  // A 📋 chip for a task or order, at the cursor (or the end), then a space
  // that isn't part of the link so typing carries on as plain text.
  const insertWorkItemLink = (item) => {
    const q = quillRef.current;
    if (!q || busyRef.current) return;
    const text = workItemLinkText(item);
    const range = q.getSelection(true);
    const at = range ? range.index : Math.max(0, q.getLength() - 1);
    q.insertText(at, text, "link", workItemHref(item), "user");
    q.insertText(at + text.length, " ", "user");
    q.formatText(at + text.length, 1, "link", false, "user");
    q.setSelection(at + text.length + 1, 0, "user");
  };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    // Idempotent: React 18 StrictMode double-invokes effects in dev, and if
    // cleanup ever races the next mount, clearing first guarantees we never
    // end up with two toolbars stacked in the same host.
    host.innerHTML = "";
    const editorEl = document.createElement("div");
    host.appendChild(editorEl);

    // onSend(body, format, files). Unformatted input goes out as plain text, so
    // it keeps working on a database without the body_format column; only a
    // message that actually carries formatting is sent as "html". A message
    // may be files only (body "").
    const send = async () => {
      const q = quillRef.current;
      if (!q) return;
      const ops = q.getContents()?.ops || [];
      const formatted = ops.some((op) => op.attributes && Object.keys(op.attributes).length > 0);
      let body;
      let format = "text";
      if (!formatted) {
        body = q.getText().trim();
      } else {
        const raw = typeof q.getSemanticHTML === "function" ? q.getSemanticHTML() : q.root.innerHTML;
        // Quill 2's getSemanticHTML turns every space into &nbsp;, which makes
        // the whole message one unbreakable run in the thread.
        const html = raw.replace(/&nbsp;/g, " ");
        if (isBlankHtml(html)) {
          body = "";
        } else {
          body = html;
          format = "html";
        }
      }
      if (!body && filesRef.current.length === 0) return;
      if (busyRef.current) return;
      const mentions = membersRef.current
        ? buildMentionsPayload(q.getText(), pickedRef.current, { isDm: isDmRef.current })
        : null;
      busyRef.current = true;
      setBusy(true);
      // Lock the draft while it sends: anything typed or attached now would be
      // wiped by the clear on success.
      const hadFocus = q.hasFocus();
      q.enable(false);
      let ok = true;
      try {
        const result = mentions
          ? await onSendRef.current(body, format, filesRef.current, mentions)
          : await onSendRef.current(body, format, filesRef.current);
        ok = result !== false;
      } catch (err) {
        // A rejecting onSend is a failed send too: keep the draft for a retry.
        ok = false;
        console.warn("Messaging: send failed", err);
      } finally {
        busyRef.current = false;
        setBusy(false);
        q.enable(true);
        // Locking can drop the editor's focus; hand it back for the next
        // message unless the user has moved on to something else meanwhile.
        const active = document.activeElement;
        if (hadFocus && quillRef.current === q && (!active || active === document.body)) {
          q.focus({ preventScroll: true });
        }
      }
      if (ok && quillRef.current) {
        quillRef.current.setContents([], "silent");
        setFiles([]);
        pickedRef.current = new Map();
        closeMention();
      }
    };
    sendRef.current = send;

    const quill = new Quill(editorEl, {
      theme: "snow",
      placeholder,
      formats: FORMATS,
      modules: { toolbar: TOOLBAR },
    });
    quillRef.current = quill;

    // Pasted files become attachments instead of being embedded, but only for
    // what Quill itself would treat as an image paste. An Excel/Word paste
    // (html plus a picture of it) is left to Quill and pastes as text. Capture
    // phase, so this runs before Quill's own paste handler, which then sees
    // defaultPrevented and leaves the event alone.
    const onPaste = (e) => {
      const pasted = Array.from(e.clipboardData?.files || []);
      if (!pasted.length || busyRef.current) return;
      const html = e.clipboardData.getData?.("text/html") || "";
      if (html && !isLoneImageHtml(html)) return;
      e.preventDefault();
      e.stopPropagation();
      addFilesRef.current(pasted);
    };
    quill.root.addEventListener("paste", onPaste, true);

    // Opens, narrows or closes the @list as the text and caret move. Only the last stretch before
    // the caret is read: a mention query is one short word.
    const onEditorChange = () => {
      const range = quill.getSelection();
      if (!membersRef.current || !range || range.length > 0) {
        if (mentionRef.current) closeMention();
        return;
      }
      const from = Math.max(0, range.index - 64);
      const found = findMentionQuery(quill.getText(from, range.index - from));
      const start = found ? from + found.start : null;
      if (start !== dismissedAtRef.current) dismissedAtRef.current = null;
      if (!found || start === dismissedAtRef.current) {
        if (mentionRef.current) closeMention();
        return;
      }
      const items = mentionSuggestions(membersRef.current, found.query, { isDm: isDmRef.current });
      if (!items.length) {
        if (mentionRef.current) closeMention();
        return;
      }
      const prev = mentionRef.current;
      const next = {
        start,
        end: range.index,
        items,
        active: prev && prev.start === start ? Math.min(prev.active, items.length - 1) : 0,
      };
      mentionRef.current = next;
      setMention(next);
    };
    quill.on("editor-change", onEditorChange);

    // Quill's keyboard module merges its own named Enter bindings (list,
    // blockquote, header, table, code-block exit, ...) into `options.bindings`
    // ahead of anything passed via `modules.keyboard.bindings`, so a
    // config-time Enter handler can still lose to a Quill default depending
    // on merge order. Unshifting onto the live bindings array after
    // construction guarantees ours run first, regardless.
    const keyboardBindings = quill.keyboard.bindings;
    keyboardBindings.Enter = keyboardBindings.Enter || [];
    keyboardBindings.Enter.unshift(
      {
        key: "Enter",
        shiftKey: false,
        handler: () => {
          send();
          return false;
        },
      },
      {
        key: "Enter",
        shiftKey: true,
        handler: (range) => {
          quill.insertText(range.index, "\n", "user");
          quill.setSelection(range.index + 1, "silent");
          return false;
        },
      },
    );

    // While the @list is open, its keys come first (ahead of the send-on-Enter above, and of
    // Quill's own Tab / arrow handling); otherwise they fall through (return true).
    const whileListOpen = (key, act) => {
      keyboardBindings[key] = keyboardBindings[key] || [];
      keyboardBindings[key].unshift({
        key,
        handler: () => {
          const open = mentionRef.current;
          if (!open) return true;
          act(open);
          return false;
        },
      });
    };
    const moveActive = (delta) => (open) => {
      const next = { ...open, active: (open.active + delta + open.items.length) % open.items.length };
      mentionRef.current = next;
      setMention(next);
    };
    whileListOpen("ArrowDown", moveActive(1));
    whileListOpen("ArrowUp", moveActive(-1));
    whileListOpen("Enter", (open) => pickMentionRef.current(open.items[open.active]));
    whileListOpen("Tab", (open) => pickMentionRef.current(open.items[open.active]));
    whileListOpen("Escape", (open) => {
      dismissedAtRef.current = open.start;
      closeMention();
    });

    return () => {
      quill.off("editor-change", onEditorChange);
      quill.root.removeEventListener("paste", onPaste, true);
      quillRef.current = null;
      sendRef.current = null;
      host.innerHTML = "";
    };
  }, [placeholder]);

  return (
    <VStack
      position="relative"
      align="stretch"
      spacing={1}
      borderTop="1px solid"
      borderColor="gray.200"
      p={3}
      onDragOver={(e) => {
        if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
      }}
      onDrop={(e) => {
        if (e.dataTransfer?.files?.length) {
          e.preventDefault();
          // Taken here: the conversation around the composer also accepts
          // drops and must not add the same files a second time.
          e.stopPropagation();
          addFiles(e.dataTransfer.files);
        }
      }}
    >
      {mention && (
        <Box
          role="listbox"
          aria-label="Mention someone"
          data-testid="mention-list"
          position="absolute"
          bottom="100%"
          left={3}
          w="280px"
          maxH="240px"
          overflowY="auto"
          bg="white"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          py={1}
          zIndex={2}
        >
          {mention.items.map((item, i) => (
            <Box
              key={`${item.kind}:${item.id}`}
              role="option"
              aria-selected={i === mention.active}
              px={3}
              py={1.5}
              cursor="pointer"
              bg={i === mention.active ? "blue.50" : "transparent"}
              // mousedown, not click: the editor keeps its focus and caret.
              onMouseDown={(e) => {
                e.preventDefault();
                pickMention(item);
              }}
              onMouseEnter={() => {
                const next = { ...mentionRef.current, active: i };
                mentionRef.current = next;
                setMention(next);
              }}
            >
              <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                @{item.label}
              </Text>
              {item.hint && (
                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                  {item.hint}
                </Text>
              )}
            </Box>
          ))}
        </Box>
      )}
      {files.length > 0 && (
        <HStack spacing={1} flexWrap="wrap">
          {files.map((f, i) => (
            <Tag key={`${f.name}-${i}`} size="sm" borderRadius="full" variant="subtle" colorScheme="blue">
              <TagLabel>{f.name} · {formatBytes(f.size)}</TagLabel>
              <TagCloseButton
                aria-label={`Remove ${f.name}`}
                isDisabled={busy}
                onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
              />
            </Tag>
          ))}
        </HStack>
      )}
      <HStack align="flex-end" spacing={2}>
        <Box
          flex="1"
          ref={hostRef}
          data-testid="chat-composer"
          sx={{
            ".ql-container": { maxH: "140px", overflowY: "auto", fontSize: "sm", borderBottomRadius: "md" },
            ".ql-toolbar": { borderTopRadius: "md", py: 1 },
            ".ql-editor": { minH: "38px" },
          }}
        />
        {!noFiles && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            data-testid="chat-file-input"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />
        )}
        {linkableItems && (
          <Popover placement="top-end" isLazy isOpen={linkOpen} onOpen={() => setLinkOpen(true)}
                   onClose={() => setLinkOpen(false)}>
            <PopoverTrigger>
              <IconButton aria-label="Link a task" title="Link a task or order" variant="ghost" isDisabled={busy}
                          icon={<Text as="span" fontSize="md" lineHeight={1}>📋</Text>} />
            </PopoverTrigger>
            <PopoverContent w="340px">
              <PopoverBody>
                <WorkItemLinkPicker
                  items={linkableItems}
                  onPick={(item) => {
                    setLinkOpen(false);
                    insertWorkItemLink(item);
                  }}
                />
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
        {!noFiles && (
          <IconButton
            aria-label="Attach files"
            title="Attach files"
            icon={<PaperclipIcon boxSize="1.25em" />}
            variant="ghost"
            isDisabled={busy}
            onClick={() => fileInputRef.current?.click()}
          />
        )}
        <IconButton
          aria-label="Send"
          icon={<ArrowForwardIcon />}
          colorScheme="blue"
          isLoading={busy}
          onClick={() => sendRef.current?.()}
        />
      </HStack>
      {note && (
        <Text fontSize="xs" color="gray.500">
          {note}
        </Text>
      )}
    </VStack>
  );
});

export default ChatComposer;
